import React from 'react';
import './index.css';
import App from './App';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';

import { createBrowserRouter, RouterProvider } from "react-router-dom";
import axios from "axios";
// MSAL imports
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from "@azure/msal-browser";
import { loginRequest, msalConfig } from './Components/authConfig';
import { MsalProvider } from '@azure/msal-react';
import { AuthProvider } from './Components/AuthContext';
import { createRoot } from 'react-dom/client';
import { UserType } from './Components/MagniTypes';
import APICalls from './Components/APICalls';

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and set up Axios interceptors
msalInstance.initialize().then(() => {
  setupAxiosRequestInterceptor(msalInstance);
  setupAxiosResponseInterceptor(msalInstance);
});

// Ensure the active account is set
if (!msalInstance.getActiveAccount()) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}

// Handle MSAL login events
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
    
    // Store account username and name if available
    localStorage.setItem('account', account.username);
    if (account.name) {
      localStorage.setItem('accountName', account.name);
    }
    
    // Create and store user object for Header2 component
    const userInfo = new UserType();
    userInfo.UserName = account.name || account.username; // Prefer the full name if available
    localStorage.setItem('user', JSON.stringify(userInfo));
    
    // Explicitly acquire and store tokens after login success
    const getTokens = async () => {
      try {
        // Clear any existing tokens first
        localStorage.removeItem('token');
        localStorage.removeItem('graphToken');
        
        // Step 1: Acquire token for your API
        const apiToken = await msalInstance.acquireTokenSilent({
          ...loginRequest, 
          account
        });
        localStorage.setItem('token', apiToken.accessToken);
        
        // Step 2: Acquire a separate token for Microsoft Graph API
        const graphToken = await msalInstance.acquireTokenSilent({
          scopes: ["User.Read"],
          account
        });
        localStorage.setItem('graphToken', graphToken.accessToken);
        
        // Clear any cached user data to force fresh fetch
        localStorage.removeItem('graphUser');
        localStorage.removeItem('graphUserTimestamp');
        
        // Step 3: Fetch user data from Graph API
        const apiCalls = new APICalls();
        await apiCalls.GetUserFromGraph();
      } catch (error) {
        console.error("Failed to acquire tokens after login", error);
      }
    };
    getTokens();
  }
});

// Define your routes using createBrowserRouter
const router = createBrowserRouter([
  {
    path: "/*",
    element: (
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <App pca={msalInstance} />
        </AuthProvider>
      </MsalProvider>
    ),
  },
]);

// Render the application with RouterProvider
const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

// Axios interceptor functions
async function setupAxiosRequestInterceptor(msalInstance: PublicClientApplication) {
  axios.interceptors.request.use(
    async (config) => {
      // Skip auth header for certain external domains like CDNs
      const skipAuthUrls = ['cdn.', 'fonts.googleapis.com', 'data:'];
      if (config.url && skipAuthUrls.some(url => config.url?.includes(url))) {
        return config;
      }
      
      let activeAccount = msalInstance.getActiveAccount();
      let attempts = 0;

      // Wait until active account is set or until we've tried a few times.
      while (!activeAccount && attempts < 3) {
        await new Promise(res => setTimeout(res, 500));  // Wait for 500ms
        activeAccount = msalInstance.getActiveAccount();
        attempts++;
      }

      if (!activeAccount) {
        return config;  // No active account after waiting
      }

      try {
        // Determine which token to use based on the request URL
        if (config.url?.includes('graph.microsoft.com')) {
          // For Graph API requests, use the Graph token
          let graphToken = localStorage.getItem('graphToken');
          
          // If no token or token might be expired, get a new one
          if (!graphToken) {
            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: activeAccount
              });
              graphToken = tokenResponse.accessToken;
              localStorage.setItem('graphToken', graphToken);
            } catch (tokenError) {
              console.error("Failed to acquire Graph token", tokenError);
              return config; // Continue without token
            }
          }
          
          config.headers['Authorization'] = `Bearer ${graphToken}`;
        } else {
          // For other API requests, use the regular token
          const token = localStorage.getItem('token');
          
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          } else {
            try {
              const tokenResponse = await msalInstance.acquireTokenSilent(loginRequest);
              localStorage.setItem('token', tokenResponse.accessToken);
              config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
            } catch (tokenError) {
              console.error("Failed to acquire API token", tokenError);
            }
          }
        }
        
        return config;
      } catch (error) {
        console.error("Failed to process request interceptor", error);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

async function setupAxiosResponseInterceptor(msalInstance: PublicClientApplication) {
  // Error response handling constants
  const RETRY_LIMIT = 3;
  const RETRY_DELAY = 2000; // 2 seconds in milliseconds
  const REDIRECT_LOCKOUT_KEY = 'auth_redirect_lockout';
  const LOCKOUT_DURATION = 10000; // 10 seconds

  // Keep track of ongoing token refresh attempts to prevent multiple simultaneous refreshes
  let isRefreshingToken = false;
  let tokenRefreshPromise: Promise<string> | null = null;

  axios.interceptors.response.use(
    async (response) => {
      return response; // If the response was successful, return it
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Only log the error once to prevent console spam
      if (!originalRequest._errorLogged) {
        console.error('API Error:', error.response?.status, originalRequest.url);
        originalRequest._errorLogged = true;
      }

      // Check if the response was a 401 and we haven't already retried too many times
      if (error.response?.status === 401 && (!originalRequest._retryCount || originalRequest._retryCount < RETRY_LIMIT)) {
        // Initialize retry count if it doesn't exist
        if (!originalRequest._retryCount) originalRequest._retryCount = 0;
        originalRequest._retryCount++;
        
        // Check if we need to prevent multiple redirects (avoid redirect loops)
        const redirectLockoutEnd = parseInt(sessionStorage.getItem(REDIRECT_LOCKOUT_KEY) || '0');
        const currentTime = new Date().getTime();
        
        try {
          // Handle token refresh - only do this once even if multiple requests fail simultaneously
          if (!isRefreshingToken) {
            isRefreshingToken = true;
            tokenRefreshPromise = refreshTokenBasedOnUrl(originalRequest.url, msalInstance);
          }
          
          // Wait for the ongoing token refresh
          if (tokenRefreshPromise) {
            const token = await tokenRefreshPromise;
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            
            // Reset the refresh state after the token was successfully acquired
            if (originalRequest._retryCount === RETRY_LIMIT) {
              isRefreshingToken = false;
              tokenRefreshPromise = null;
            }
            
            // Retry the original request
            return axios(originalRequest);
          }
        } catch (refreshError) {
          console.error("Token refresh failed", refreshError);
          
          // Reset refresh state
          isRefreshingToken = false;
          tokenRefreshPromise = null;
          
          // Only redirect if we're not in a lockout period and not a Graph API request
          if (currentTime > redirectLockoutEnd && !originalRequest.url?.includes('graph.microsoft.com')) {
            // Set lockout to prevent multiple redirects
            sessionStorage.setItem(REDIRECT_LOCKOUT_KEY, (currentTime + LOCKOUT_DURATION).toString());
            
            // Redirect to login if we've exhausted retries
            if (originalRequest._retryCount >= RETRY_LIMIT) {
              console.log("Exhausted retry attempts, redirecting to login");
              msalInstance.loginRedirect(loginRequest);
            }
          }
        }
      }

      // If response was not a 401 or we already retried too many times, just reject the promise.
      return Promise.reject(error);
    }
  );
}

// Helper function to refresh the appropriate token based on the request URL
async function refreshTokenBasedOnUrl(url: string | undefined, msalInstance: PublicClientApplication): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error("No active account");
  
  try {
    if (url?.includes('graph.microsoft.com')) {
      // Refresh Graph API token
      const graphTokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account
      });
      
      localStorage.setItem('graphToken', graphTokenResponse.accessToken);
      return graphTokenResponse.accessToken;
    } else {
      // Refresh API token
      const tokenResponse = await msalInstance.acquireTokenSilent(loginRequest);
      localStorage.setItem('token', tokenResponse.accessToken);
      return tokenResponse.accessToken;
    }
  } catch (error) {
    console.error("Failed to refresh token silently", error);
    throw error;
  }
}